import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  wards: [
    {
      id: Date.now(),
      name: 'ICU',
      createdAt: new Date().toISOString()
    }
  ],
  patients: [],
  beds: [],
  tasks: [],
  admittedPatients: [],
  dischargePatients: [
    {
      bedName: 'test',
      patient: {
        full_name: 'husnain bene',
        file_no: '700012'
      },
      ward: {
        id: 1751890008810,
        name: 'ICU',
        createdAt: '2025-07-07T12:06:48.810Z'
      },
      id: 'OP-0012',
      status: 'Addmitted',
      notes: 'Patient recovered and discharged with prescription.'
    },
    {
      bedName: 'test',
      patient: {
        full_name: 'tayab mohammed',
        file_no: '700002'
      },
      ward: {
        id: 1751890008810,
        name: 'ICU',
        createdAt: '2025-07-07T12:06:48.810Z'
      },
      id: 'OP-0002',
      status: 'Addmitted',
      notes: 'Patient recovered and discharged with prescription.'
    }
  ],
  transferedPatients: [
    {
      bedName: 'test',
      patient: {
        full_name: 'husnain bene',
        file_no: '700012'
      },
      ward: {
        id: 1751890008810,
        name: 'ICU',
        createdAt: '2025-07-07T12:06:48.810Z'
      },
      id: 'OP-0012',
      status: 'Addmitted',
      notes: 'Transferred to ICU due to deterioration in condition.'
    },
    {
      bedName: 'test',
      patient: {
        full_name: 'tayab mohammed',
        file_no: '700002'
      },
      ward: {
        id: 1751890008810,
        name: 'General',
        createdAt: '2025-07-07T12:06:48.810Z'
      },
      id: 'OP-0002',
      status: 'Addmitted',
      notes: 'Shifted to General ward for continued observation.'
    }
  ],
  floorPlans: {},
  selectedWardId: null,
  selectedPatientId: null,
  viewMode: 'ward',
  admittingPatientData: null
};

// Helper function to ensure state has proper structure
const ensureProperState = state => {
  if (!state.tasks || !Array.isArray(state.tasks)) {
    state.tasks = [];
  }
  if (!state.wards || !Array.isArray(state.wards)) {
    state.wards = [];
  }
  if (!state.patients || !Array.isArray(state.patients)) {
    state.patients = [];
  }
  if (!state.beds || !Array.isArray(state.beds)) {
    state.beds = [];
  }
  if (!state.floorPlans || typeof state.floorPlans !== 'object') {
    state.floorPlans = {};
  }
};

const wardManagementSlice = createSlice({
  name: 'wardManagement',
  initialState,
  reducers: {
    addWard: (state, action) => {
      const newWard = {
        id: Date.now(),
        name: action.payload.name,
        createdAt: new Date().toISOString()
      };
      state.wards.push(newWard);
    },
    addPatient: (state, action) => {
      const newPatient = action.payload;
      const filteredPatients = state.patients.filter(patient => patient.patient_id !== newPatient.patient_id);
      state.patients = [...filteredPatients, newPatient];
    },
    updatePatient: (state, action) => {
      const index = state.patients.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.patients[index] = { ...state.patients[index], ...action.payload };
      }
    },
    addBed: (state, action) => {
      state.beds.push({
        id: action.payload.id || Date.now(),
        name: action.payload.name,
        wardId: action.payload.wardId,
        availability: action.payload.availability,
        patientId: action.payload.patientId || null,
        position: {
          x: action.payload.x,
          y: action.payload.y
        },
        rotation: 0, // Default rotation
        createdAt: new Date().toISOString()
      });
    },
    updateBed: (state, action) => {
      const index = state.beds.findIndex(b => b.id === action.payload.id);
      if (index !== -1) {
        state.beds[index] = { ...state.beds[index], ...action.payload };
      }
    },
    updateBedPosition: (state, action) => {
      const index = state.beds.findIndex(b => b.id === action.payload.id);
      if (index !== -1) {
        state.beds[index].position = action.payload.position;
      }
    },
    rotateBed: (state, action) => {
      const index = state.beds.findIndex(b => b.id === action.payload);
      if (index !== -1) {
        const currentRotation = state.beds[index].rotation || 0;
        state.beds[index].rotation = currentRotation === 0 ? 90 : 0;
      }
    },
    setFloorPlan: (state, action) => {
      state.floorPlans[action.payload.wardId] = action.payload.floorPlan;
    },
    deleteWard: (state, action) => {
      state.wards = state.wards.filter(w => w.id !== action.payload);
      // Also remove beds and reassign patients
      state.beds = state.beds.filter(b => b.wardId !== action.payload);
      state.patients = state.patients.map(p => (p.wardId === action.payload ? { ...p, wardId: null, bedId: null } : p));
      delete state.floorPlans[action.payload];
    },
    deleteBed: (state, action) => {
      state.beds = state.beds.filter(b => b.id !== action.payload?.id);
      state.patients = state.patients.map(p => (p.bedId === action.payload?.id ? { ...p, bedId: null } : p));
      state.admittedPatients = state?.admittedPatients?.filter(data => data?.id != action?.payload?.patientId);
    },
    deletePatient: (state, action) => {
      const patientToDelete = state.patients.find(p => p.id === action.payload);
      if (patientToDelete && patientToDelete.bedId) {
        // If patient had a bed, make it available
        const bedIndex = state.beds.findIndex(b => b.patientId === patientToDelete.id);
        if (bedIndex !== -1) {
          state.beds[bedIndex].patientId = null;
          state.beds[bedIndex].availability = 'Available';
        }
      }
      state.patients = state.patients.filter(p => p.id !== action.payload);
    },
    setSelectedWard: (state, action) => {
      state.selectedWardId = action.payload;
    },
    addTask: (state, action) => {
      // Ensure proper state structure
      ensureProperState(state);

      state.tasks.push({
        id: Date.now(),
        title: action.payload.title,
        description: action.payload.description,
        priority: action.payload.priority,
        patientId: action.payload.patientId,
        wardId: action.payload.wardId,
        date: action.payload.date,
        status: action.payload.status,
        dueDate: action.payload.dueDate,
        createdAt: new Date().toISOString()
      });
    },
    updateTask: (state, action) => {
      // Ensure tasks array exists
      if (!state.tasks) {
        state.tasks = [];
      }
      const index = state.tasks.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...action.payload };
      }
    },
    deleteTask: (state, action) => {
      // Ensure tasks array exists
      if (!state.tasks) {
        state.tasks = [];
        return;
      }
      state.tasks = state.tasks.filter(t => t.id !== action.payload);
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatientId = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setAdmittingPatientData: (state, action) => {
      state.admittingPatientData = action.payload;
    },
    // Reset state action for debugging
    resetWardManagementState: () => {
      return initialState;
    },
    addNewAddmittedPatient: (state, action) => {
      state.admittedPatients = [action.payload, ...state.admittedPatients];
    },
    updateAddmittedPatient: (state, action) => {
      const { prevId, ...updatedPatient } = action.payload;
      const index = state.admittedPatients.findIndex(patient => patient.id === prevId);
      if (index !== -1) {
        state.admittedPatients[index] = updatedPatient;
      }
    },
    addNewDischargePatient: (state, action) => {
      state.dischargePatients = [...state?.dischargePatients, action?.payload];
      state.admittedPatients = state?.admittedPatients?.filter(data => data?.id != action?.payload?.id);
      state.beds = state?.beds?.filter(data => data?.patientId != action?.payload?.id);
    },
    addNewTransferPatient: (state, action) => {
      state.transferedPatients = [...state?.transferedPatients, action?.payload];
      state.admittedPatients = state?.admittedPatients?.filter(data => data?.id != action?.payload?.id);
      state.beds = state?.beds?.filter(data => data?.patientId != action?.payload?.id);
    }
  }
});

export const {
  addWard,
  addPatient,
  updatePatient,
  addBed,
  updateBed,
  updateBedPosition,
  rotateBed,
  setFloorPlan,
  deleteWard,
  deleteBed,
  deletePatient,
  setSelectedWard,
  addTask,
  updateTask,
  deleteTask,
  setSelectedPatient,
  setViewMode,
  resetWardManagementState,
  setAdmittingPatientData,
  addNewAddmittedPatient,
  updateAddmittedPatient,
  addNewDischargePatient,
  addNewTransferPatient
} = wardManagementSlice.actions;

export default wardManagementSlice.reducer;
